import React, { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

/**
 * Drop-in replacement for FormDataConsumer inside SimpleFormIterator.
 *
 * react-admin's FormDataConsumer uses useWatch() with no field name, so every
 * keystroke re-renders every consumer (and spreads huge form values). This
 * version only watches the current iterator item path.
 *
 * SimpleFormIterator clones this with source=`${arraySource}.${index}`.
 */
export const ScopedIteratorFields = ({ children, source, index, ...rest }) => {
  const { getValues } = useFormContext();
  const scopedFormData = useWatch({ name: source });

  const getSource = useCallback(
    (scopedSource) => (source ? `${source}.${scopedSource}` : scopedSource),
    [source],
  );

  if (typeof children !== 'function') {
    return null;
  }

  const result =
    typeof index !== 'undefined' && source
      ? children({
          // Snapshot only — do not subscribe to the whole form
          formData: getValues(),
          scopedFormData,
          getSource,
          source,
          index,
          ...rest,
        })
      : children({
          formData: getValues(),
          getSource: (scopedSource) => scopedSource,
          ...rest,
        });

  return result === undefined ? null : result;
};
